import {
	Box,
	Button,
	Checkbox,
	FormControl,
	InputLabel,
	NativeSelect,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	useTheme,
} from "@mui/material"
import useMediaQuery from "@mui/material/useMediaQuery"
import * as yup from "yup"

import { useFormik } from "formik"
import { useEffect, useState } from "react"
import ConfirmationDialog from "../../components/ConfirmationDialog"
import Header from "../../components/Header"
import ModulePermissionsApi from "../../services/api/ModulePermissions"
import ProjectModuleApi from "../../services/api/ProjectModules"
import ProjectPermissionApi from "../../services/api/ProjectPermission"
import ActionApi from "../../services/api/actions"
import RoleApi from "../../services/api/roles"
import { tokens } from "../../theme"

const ModulePermissions = () => {
	const isNonMobile = useMediaQuery("(min-width:600px)")
	const theme = useTheme()
	const colors = tokens(theme.palette.mode)
	const [isEdit, setIsEdit] = useState(false)
	const [roles, setRoles] = useState([])
	const [roleId, setRoleId] = useState()
	const [projectId, setProjectId] = useState()
	const [roleProjects, setRoleProjects] = useState([])
	const [projectModules, setProjectModules] = useState([])
	const [actions, setActions] = useState([])
	const [rolePermittedModules, setRolePermittedModules] = useState([])

	useEffect(() => {
		fetchRoles()
		fetchActions()
	}, [])

	useEffect(() => {
		let modules = projectModules.map((module) => ({
			id: module.id,
			name: module.name,
			actions: actions.map((action, index) => {
				return findModuleActionHasPermission(module.id, index + 1)
			}),
		}))

		formik.setValues({
			...formik.values,
			modules: modules,
		})
	}, [projectModules])

	const findModuleActionHasPermission = (moduleId, actionId) => {
		const permitted = rolePermittedModules.find((module) => {
			return module.moduleId === moduleId && module.actionId === actionId
		})

		return permitted ? true : false
	}

	const formik = useFormik({
		initialValues: initialValues,
		validationSchema: checkoutSchema,

		onSubmit: async (values, { resetForm }) => {
			console.log(values)
			createEntity(values)

			resetForm({ values: { ...initialValues, role_id: roleId } })
			fetchPermitedModules(roleId)
			handleAfterReset()
		},
	})

	const createEntity = async (values) => {
		const response = await ModulePermissionsApi().create(values)

		console.log(createdSuccessMsg, response)
		fetchRoleProjects(roleId)
	}

	const handleAfterReset = () => {
		setIsEdit(false)
	}

	const fetchRoles = async () => {
		try {
			const data = await RoleApi().getAll()

			setRoles(data.results)
		} catch (error) {
			console.log("Not loading roles", error)
		}
	}

	const fetchActions = async () => {
		try {
			const data = await ActionApi().getAll()

			setActions(data.results)
		} catch (error) {
			console.log("Not loading projects", error)
		}
	}

	const handleRoleChange = (event) => {
		setRoleId(event.target.value)
		fetchRoleProjects(event.target.value)
		fetchPermitedModules(event.target.value)
		formik.handleChange(event)
	}

	const handleProjectChange = (event) => {
		setProjectId(event.target.value)
		fetchProjectModules(event.target.value)
		formik.handleChange(event)
	}

	const [isConfirmationOpen, setConfirmationOpen] = useState(false)
	const [selectedId, setSelectedId] = useState("")

	const destroy = (row) => {
		setSelectedId(row.id)
		handleOpenConfirmation()
	}

	const handleOpenConfirmation = () => {
		setConfirmationOpen(true)
	}

	const handleCloseConfirmation = () => {
		setConfirmationOpen(false)
	}

	const handleConfirmAction = () => {
		try {
			const response = ProjectPermissionApi().destroy(selectedId)

			console.log(deletedSuccessMsg, response)
			fetchRoleProjects(roleId)
		} catch (error) {
			console.log(deleteErrorMsg, error)
		}

		handleCloseConfirmation()
	}

	const fetchRoleProjects = async (roleId) => {
		try {
			const data = await ProjectPermissionApi().getByRoleId(roleId)

			setRoleProjects(data)
		} catch (error) {
			setRoleProjects([])
			if (error.response.status === 404) {
				console.log("This role has no projects.")
			} else {
				console.log("Loading error", error.response)
			}
		}
	}

	const fetchPermitedModules = async (roleId) => {
		try {
			const data = await ModulePermissionsApi().getByRoleId(roleId)

			setRolePermittedModules(data)
		} catch (error) {
			setRolePermittedModules([])
			if (error.response.status === 404) {
				console.log("This role has no projects.")
			} else {
				console.log("Loading error", error.response)
			}
		}
	}

	const fetchProjectModules = async (projectId) => {
		try {
			const data = await ProjectModuleApi().getByProjectId(projectId)

			setProjectModules(data)
		} catch (error) {
			setProjectModules([])
			if (error.response.status === 404) {
				console.log("This project has no modules.")
			} else {
				console.log("Loading error", error.response)
			}
		}
	}

	return (
		<>
			<Box m="20px">
				<Header title={`${formName}`} subtitle="" />
				<form onSubmit={formik.handleSubmit}>
					<Box
						display="grid"
						gap="30px"
						gridTemplateColumns="repeat(4, minmax(0, 1fr))"
						sx={{
							"& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
						}}
					>
						<Box sx={{ gridColumn: "span 1" }}>
							<FormControl fullWidth>
								<InputLabel variant="standard" htmlFor="uncontrolled-native">
									Role
								</InputLabel>
								<NativeSelect
									value={formik.values.role_id}
									onChange={handleRoleChange}
									inputProps={{
										name: "role_id",
									}}
								>
									<option value=""></option>
									{roles
										? roles.map((role) => (
												<option value={role.id}>{role.name}</option>
										  ))
										: null}
								</NativeSelect>
							</FormControl>

							<Box
								m="20px 0 0 0"
								display="grid"
								gap="30px"
								gridTemplateColumns="repeat(4, minmax(0, 1fr))"
								sx={{
									"& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
								}}
							>
								<FormControl
									fullWidth
									sx={{ gridColumn: "span 4", marginTop: "8px" }}
								>
									<InputLabel variant="standard" htmlFor="uncontrolled-native">
										Project
									</InputLabel>
									<NativeSelect
										value={formik.values.project_id}
										onChange={handleProjectChange}
										inputProps={{
											name: "project_id",
										}}
									>
										<option value=""></option>
										{roleProjects
											? roleProjects.map((project) => (
													<option value={project.projectId}>
														{project.projectName}
													</option>
											  ))
											: null}
									</NativeSelect>
								</FormControl>
							</Box>
							<Box display="flex" justifyContent="end" mt="20px">
								<Button type="submit" color="secondary" variant="contained">
									{isEdit ? "Update" : "Save"}
								</Button>
							</Box>
						</Box>

						<Box sx={{ gridColumn: "span 3" }}>
							<TableContainer component={Paper}>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>
												<Typography variant="h6" color="primary">
													{"Module"}
												</Typography>
											</TableCell>
											{formik.values.modules[0]?.actions.map((_, index) => (
												<TableCell key={index}>
													<Typography variant="h6" color="primary">
														{actions[index].name}
													</Typography>
												</TableCell>
											))}
										</TableRow>
									</TableHead>
									<TableBody>
										{formik.values.modules.map((module, moduleIndex) => (
											<TableRow key={moduleIndex}>
												<TableCell>
													<Typography variant="h6" color="primary">
														{module.name}
													</Typography>
												</TableCell>
												{module.actions.map((action, actionIndex) => (
													<TableCell key={actionIndex}>
														<Checkbox
															checked={action}
															onChange={(e) => {
																const newModules = [...formik.values.modules]
																newModules[moduleIndex].actions[actionIndex] =
																	e.target.checked
																formik.setFieldValue("modules", newModules)
															}}
															inputProps={{ "aria-label": "controlled" }}
															sx={{
																color: colors.blueAccent[800],
																"&.Mui-checked": {
																	color: colors.blueAccent[600],
																},
																"& .MuiSvgIcon-root": { fontSize: 28 },
															}}
														/>
													</TableCell>
												))}
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
							{formik.touched.modules && formik.errors.modules ? (
								<div className="error">{formik.errors.modules}</div>
							) : null}
						</Box>
					</Box>
				</form>
			</Box>
			<ConfirmationDialog
				open={isConfirmationOpen}
				onClose={handleCloseConfirmation}
				onConfirm={handleConfirmAction}
			/>
		</>
	)
}

const checkoutSchema = yup.object().shape({
	role_id: yup.number().required("required"),
	project_id: yup.number().required("required"),
	modules: yup
		.array()
		.of(
			yup.object().shape({
				id: yup.number(),
				name: yup.string(),
				actions: yup.array().of(yup.boolean()),
			})
		)
		.required("At least one module must be selected"),
})

const initialValues = {
	role_id: "",
	project_id: "",
	modules: [],
}

const formName = "Module Permission"
const createdSuccessMsg = `${formName} created:`
const updatedSuccessMsg = `${formName} updated:`
const deletedSuccessMsg = `${formName} deleted successfully: `
const deleteErrorMsg = `Not deleted ${formName}`

export default ModulePermissions
